import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatasetStore } from '@/store/dataset-store';
import { useFiltersStore } from '@/store/filters-store';
import { useModeStore } from '@/store/mode-store';
import {
  draftsEqual,
  initialDrafts,
  isEmptyDraft,
  usePinnedFiltersStore,
  type SlotDraft,
} from '@/store/pinned-filters-store';
import type { ColumnMeta } from '@/types/dataset';
import type { FilterExpression } from '@/types/query';
import { kindOf } from '../column-types';
import { TextSlot } from './TextSlot';
import { DateSlot } from './DateSlot';
import { NumericSlot } from './NumericSlot';

/**
 * Horizontal row of pinned filter slots, one per user-selected column, plus a
 * batched Apply / Reset pair. Every slot writes its local edits into a
 * per-column draft; nothing hits the global filters list until the user
 * clicks Apply. The pinned-column picker lives in the sidebar.
 */
export function PinnedFilterBar(): JSX.Element | null {
  const { t } = useTranslation();
  const activeId = useDatasetStore((s) => s.activeDatasetId);
  const datasets = useDatasetStore((s) => s.datasets);
  const filters = useFiltersStore((s) => s.filters);
  const addFilter = useFiltersStore((s) => s.addFilter);
  const removeFilter = useFiltersStore((s) => s.removeFilter);

  const pinnedCols = usePinnedFiltersStore((s) => s.columns);
  const drafts = usePinnedFiltersStore((s) => s.drafts);
  const pinnedDatasetId = usePinnedFiltersStore((s) => s.datasetId);
  const pinnedMode = usePinnedFiltersStore((s) => s.mode);
  const loadForDataset = usePinnedFiltersStore((s) => s.loadForDataset);
  const resetDrafts = usePinnedFiltersStore((s) => s.resetDrafts);
  const clear = usePinnedFiltersStore((s) => s.clear);
  const mode = useModeStore((s) => s.mode);

  const columns: ColumnMeta[] = useMemo(
    () => (activeId ? datasets.get(activeId)?.columns ?? [] : []),
    [activeId, datasets],
  );

  const columnMetaByName = useMemo(() => {
    const m = new Map<string, ColumnMeta>();
    for (const c of columns) m.set(c.name, c);
    return m;
  }, [columns]);

  // Hydrate pinned columns from IndexedDB whenever the active
  // (dataset, mode) changes. `filters` is NOT a dep — we seed drafts
  // once per (dataset, mode).
  useEffect(() => {
    if (!activeId) {
      clear();
      return;
    }
    if (pinnedDatasetId === activeId && pinnedMode === mode) return;
    void loadForDataset(activeId, mode, initialDrafts(filters, []));
  }, [
    activeId,
    pinnedDatasetId,
    pinnedMode,
    mode,
    loadForDataset,
    clear,
    filters,
  ]);

  // Seed missing draft entries when the pinned column list grows. We do
  // NOT overwrite existing drafts — that would clobber in-flight edits.
  useEffect(() => {
    for (const col of pinnedCols) {
      if (drafts[col] === undefined) {
        const seed: SlotDraft | null = readCommittedManualFilter(filters, col);
        usePinnedFiltersStore.getState().setDraft(col, seed);
      }
    }
  }, [pinnedCols, drafts, filters]);

  if (!activeId) return null;

  // Dirty = any pinned slot whose draft differs from its committed filter.
  const dirtyColumns = pinnedCols.filter((col) => {
    const committed = readCommittedManualFilter(filters, col);
    return !draftsEqual(drafts[col] ?? null, committed);
  });
  const isDirty = dirtyColumns.length > 0;

  function handleApply(): void {
    for (const col of dirtyColumns) {
      const committed = readCommittedManualFilter(filters, col);
      const next = drafts[col] ?? null;
      if (committed) removeFilter(col, committed.value);
      if (!isEmptyDraft(next) && next) {
        const expr: FilterExpression = {
          column: col,
          operator: next.operator,
          value: next.value,
          source: 'manual',
        };
        addFilter(expr);
      }
    }
  }

  function handleReset(): void {
    resetDrafts(initialDrafts(filters, pinnedCols));
  }

  return (
    <div
      className="flex flex-col md:flex-row md:items-end gap-3 px-3 py-2 rounded-lg"
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      {/* Mobil ve tablet uyumlu grid yapısı */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 flex-1 min-w-0 items-start w-full">
        {pinnedCols.length === 0 && (
          <span
            className="text-xs self-center col-span-full"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('filters.pinned.noFiltersHint')}
          </span>
        )}

        {pinnedCols.map((col) => {
          const meta = columnMetaByName.get(col);
          if (!meta) return null;
          const kind = kindOf(meta.sqlType);
          return (
            <div key={col} className="flex flex-col gap-1 min-w-0">
              <span
                className="text-[11px] font-medium truncate"
                style={{ color: 'var(--color-fg)' }}
                title={col}
              >
                {col}
              </span>
              {kind === 'text' && (
                <TextSlot datasetId={activeId} column={col} />
              )}
              {kind === 'date' && <DateSlot column={col} />}
              {kind === 'numeric' && (
                <NumericSlot column={col} sqlType={meta.sqlType} />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">
        <button
          type="button"
          onClick={handleApply}
          disabled={!isDirty}
          className="p-1.5 rounded text-white disabled:opacity-40"
          style={{ background: 'var(--color-accent)' }}
          title={
            isDirty
              ? t('filters.pinned.applyDirtyTitle', { n: dirtyColumns.length })
              : t('filters.pinned.applyTitle')
          }
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={!isDirty}
          className="p-1.5 rounded disabled:opacity-40"
          style={{
            border: '1px solid var(--color-border)',
            color: 'var(--color-muted)',
          }}
          title={t('filters.pinned.resetTitle')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
            <path d="M3 3v5h5"></path>
          </svg>
        </button>
      </div>
    </div>
  );
}

/** Committed manual filter for a column, or null if none. */
function readCommittedManualFilter(
  filters: readonly FilterExpression[],
  column: string,
): SlotDraft | null {
  const f = filters.find(
    (x) => x.column === column && x.source !== 'drilldown',
  );
  if (!f) return null;
  return { operator: f.operator, value: f.value };
}
